/**
 * Flow Sender Utilities
 *
 * Handles which sender account a flow's "Send Email" step uses. Supported modes
 * (configured per Send-Email node in the flow builder):
 *   - 'default' : keep the existing behaviour (global lastUsedSender). No override.
 *   - 'fixed'   : always use one specific account.
 *   - 'random'  : rotate evenly across ALL sender accounts.
 *   - 'choose'  : rotate evenly across a chosen subset of accounts (senderPool).
 *
 * Even distribution is achieved with a persistent round-robin counter in RTDB
 * (`/World/{userId}/settings/flowSenderRotation/{flowId}`), incremented via a
 * transaction. The picked account is persisted on the contact's flow state
 * (`flowSenderId`) so the same contact keeps its sender across every email step
 * of the flow (hybrid: assigned at flow start, backfilled at send time).
 */

import { ref, runTransaction } from 'firebase/database';
import { doc, updateDoc } from 'firebase/firestore';
import { firestore, database } from '../firebaseConfig';
import { ACTIVE_EMAIL_ACTION_TYPES } from '../shared/flowTouchpoints';

/**
 * Extract the flow-level sender config from a flow definition.
 * Returns the config of the first email step that overrides the default sender,
 * or null when the flow relies purely on the default sender behaviour.
 *
 * @param {object} flow - Flow definition with steps[]
 * @returns {{senderMode:string, senderId:string, senderPool:string[]}|null}
 */
export const getFlowSenderConfig = (flow) => {
  if (!flow?.steps || !Array.isArray(flow.steps)) return null;
  for (const step of flow.steps) {
    const isEmailStep = step?.nodeType === 'action' && ACTIVE_EMAIL_ACTION_TYPES.includes(step?.type);
    if (!isEmailStep) continue;
    const senderMode = step.senderMode || 'default';
    if (senderMode !== 'default') {
      return {
        senderMode,
        senderId: step.senderId || '',
        senderPool: Array.isArray(step.senderPool) ? step.senderPool : [],
      };
    }
  }
  return null;
};

/**
 * Round-robin index via a persistent RTDB counter (transaction-safe).
 *
 * @param {string} userId
 * @param {string} flowId
 * @param {number} poolSize
 * @returns {Promise<number>} Index in [0, poolSize)
 */
export const nextRotationIndex = async (userId, flowId, poolSize) => {
  if (!userId || !flowId || !poolSize || poolSize < 1) return 0;
  const counterRef = ref(database, `World/${userId}/settings/flowSenderRotation/${flowId}`);
  let idx = 0;
  try {
    await runTransaction(counterRef, (current) => {
      const c = typeof current === 'number' && current >= 0 ? current : 0;
      idx = c % poolSize;
      return c + 1;
    });
  } catch (err) {
    console.error('[FlowSender] Rotation transaction failed:', err);
    idx = Math.floor(Math.random() * poolSize);
  }
  return idx;
};

/**
 * Resolve the account id (Firestore emailAccounts doc id) for a flow sender
 * config, applying round-robin rotation for random/choose modes.
 *
 * @param {string} userId
 * @param {string} flowId
 * @param {{senderMode:string, senderId:string, senderPool:string[]}} senderConfig
 * @param {string[]} allAccountIds - All available account ids for the user
 * @returns {Promise<string|null>} Account id, or null when nothing should override
 */
export const resolveFlowSenderAccountId = async (userId, flowId, senderConfig, allAccountIds = []) => {
  if (!senderConfig) return null;
  const { senderMode, senderId, senderPool } = senderConfig;

  if (senderMode === 'fixed') {
    return senderId && allAccountIds.includes(senderId) ? senderId : (senderId || null);
  }

  let pool = [];
  if (senderMode === 'choose') {
    pool = (senderPool || []).filter((id) => allAccountIds.includes(id));
  } else if (senderMode === 'random') {
    pool = allAccountIds;
  }

  if (!pool || pool.length === 0) return null;

  const idx = await nextRotationIndex(userId, flowId, pool.length);
  return pool[idx] || null;
};

/**
 * Resolve the sender EMAIL ADDRESS to use for a contact currently on a flow's
 * Send-Email step. Reuses an already-assigned flowSenderId when present, else
 * rotates a new pick and persists it on the contact for stability.
 *
 * @param {object}   params
 * @param {string}   params.userId
 * @param {string}   params.contactId
 * @param {object}   params.flowConfig - { senderMode, senderId, senderPool, flowSenderId, flowId, flowField }
 * @param {Array}    params.senderEmails - Loaded accounts [{ id, email, ... }]
 * @returns {Promise<string|null>} Email address, or null to keep default behaviour
 */
export const resolveFlowSenderEmail = async ({ userId, contactId, flowConfig, senderEmails }) => {
  if (!flowConfig || !Array.isArray(senderEmails) || senderEmails.length === 0) return null;

  const { senderMode, senderId, senderPool, flowSenderId, flowId, flowField = 'warmupFlow' } = flowConfig;
  if (!senderMode || senderMode === 'default') return null;

  const byId = (id) => senderEmails.find((s) => s.id === id);

  // Fixed account: deterministic, no rotation / persistence needed.
  if (senderMode === 'fixed') {
    const acc = byId(senderId);
    return acc?.email || null;
  }

  // Reuse a previously assigned sender for this contact when still valid.
  if (flowSenderId) {
    const acc = byId(flowSenderId);
    if (acc) return acc.email;
  }

  // Build the rotation pool.
  let poolIds;
  if (senderMode === 'choose') {
    poolIds = (senderPool || []).filter((id) => byId(id));
  } else {
    poolIds = senderEmails.map((s) => s.id);
  }
  if (!poolIds || poolIds.length === 0) return null;

  const idx = await nextRotationIndex(userId, flowId, poolIds.length);
  const chosenId = poolIds[idx];
  const acc = byId(chosenId);
  if (!acc) return null;

  // Persist the assignment on the contact flow state for future email steps.
  try {
    if (userId && contactId && flowField) {
      await updateDoc(doc(firestore, `World/${userId}/db/${contactId}`), {
        [`${flowField}.flowSenderId`]: chosenId,
      });
    }
  } catch (err) {
    console.error('[FlowSender] Failed to persist flowSenderId:', err);
  }

  return acc.email;
};

const flowSenderUtils = {
  getFlowSenderConfig,
  nextRotationIndex,
  resolveFlowSenderAccountId,
  resolveFlowSenderEmail,
};

export default flowSenderUtils;
